import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../Firebase/firebaseconfig';
import { FaArrowLeft, FaFileCsv, FaMoneyCheckAlt, FaSearch } from 'react-icons/fa';
import { calcularPendienteDevolver } from '../../helpers/devolucion';
import { useUserContext } from '../GestionDeUsuarios/UserContext';
import './DeudasCliente.css';

const money = (v) =>
  Number(v ?? 0).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0
  });

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const text = (v) => (v ?? '').toString();

const getImporte = (d) => d.monto ?? d.importe ?? 0;
const getMensual = (d) => d.mensualidad ?? d.pagoMensual ?? 0;

const getNombrePersona = (data = {}) =>
  [data?.nombre, data?.apellidos].filter(Boolean).join(' ').trim() ||
  data?.displayName ||
  data?.email ||
  '';

const isDevolucionDeuda = (d) => {
  const estado = String(d?.estado || '').toLowerCase().trim();

  return !!(
    d?.devolucion === true ||
    d?.esDevolucion === true ||
    estado === 'en_devolucion' ||
    estado === 'devuelta' ||
    estado === 'cerrada_devolucion'
  );
};

const calcularTotalPagadoVenta = (deuda) => {
  const pagos = Array.isArray(deuda?.pagos) ? deuda.pagos : [];
  let total = 0;
  let engancheEnPagos = false;

  for (const p of pagos) {
    const cantidad = num(p?.cantidad);
    const tipo = String(p?.tipo || '').toLowerCase();

    if (cantidad > 0) {
      total += cantidad;
    }

    if (tipo === 'enganche' && cantidad > 0) {
      engancheEnPagos = true;
    }
  }

  if (!engancheEnPagos) {
    total += num(deuda?.enganche);
  }

  return total;
};

const calcularSaldoVenta = (deuda) => {
  const montoTotal = num(getImporte(deuda));
  const totalPagado = calcularTotalPagadoVenta(deuda);
  return Math.max(montoTotal - totalPagado, 0);
};

const calcularTotalDevuelto = (deuda) => {
  const pagos = Array.isArray(deuda?.pagos) ? deuda.pagos : [];

  return pagos.reduce((acc, p) => {
    const cantidad = num(p?.cantidad);
    const tipo = String(p?.tipo || '').toLowerCase();

    if (tipo === 'devolucion_pago' && cantidad < 0) {
      return acc + Math.abs(cantidad);
    }

    return acc;
  }, 0);
};

const resolverEstadoDeuda = (deuda, saldoCalculado) => {
  const estadoRaw = String(deuda?.estado || '').toLowerCase().trim();
  const esDevolucion = !!deuda?.esDevolucionCalculada;

  if (['cancelada', 'cancelado', 'anulada', 'anulado'].includes(estadoRaw)) {
    return {
      key: 'cancelada',
      label: 'Cancelada',
      colorClass: 'badge-cancelada'
    };
  }

  if (esDevolucion) {
    if (num(saldoCalculado) > 0) {
      return {
        key: 'devolucion_pendiente',
        label: 'Devolución',
        colorClass: 'badge-pendiente'
      };
    }

    return {
      key: 'devuelta',
      label: 'Devuelta',
      colorClass: 'badge-pagada'
    };
  }

  if (num(saldoCalculado) > 0) {
    return {
      key: 'pendiente',
      label: 'Pendiente',
      colorClass: 'badge-pendiente'
    };
  }

  return {
    key: 'pagada',
    label: 'Pagada',
    colorClass: 'badge-pagada'
  };
};

const DeudasCliente = () => {
  const { id: clienteId } = useParams();
  const navigate = useNavigate();
  const { can } = useUserContext();

  const [cliente, setCliente] = useState(null);
  const [deudas, setDeudas] = useState([]);
  const [responsablesMap, setResponsablesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [orden, setOrden] = useState('concepto');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const puedeVerDeudas = can('deudas.ver');
  const puedeRegistrarPago = can('deudas.registrarPago');
  const puedeExportar = can('reportes.exportar') || can('deudas.descargarPDF');

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);

      try {
        const clientePromise = getDoc(doc(db, 'clientes', clienteId));
        const deudasPromise = getDocs(
          query(collection(db, 'deudas'), where('clienteId', '==', clienteId))
        );

        const [cliSnap, deudasSnap] = await Promise.all([clientePromise, deudasPromise]);

        if (!isMounted) return;

        setCliente(cliSnap.exists() ? { id: cliSnap.id, ...cliSnap.data() } : null);

        const deudasData = deudasSnap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }));

        setDeudas(deudasData);

        const responsableIds = [
          ...new Set(deudasData.map((d) => d?.responsableId).filter(Boolean))
        ];

        if (!responsableIds.length) {
          setResponsablesMap({});
          return;
        }

        const responsablesEntries = await Promise.all(
          responsableIds.map(async (rid) => {
            try {
              const userSnap = await getDoc(doc(db, 'usuarios', rid));
              if (userSnap.exists()) {
                return [
                  rid,
                  {
                    id: rid,
                    origen: 'usuario',
                    ...userSnap.data()
                  }
                ];
              }

              const clienteSnapFallback = await getDoc(doc(db, 'clientes', rid));
              if (clienteSnapFallback.exists()) {
                return [
                  rid,
                  {
                    id: rid,
                    origen: 'cliente',
                    ...clienteSnapFallback.data()
                  }
                ];
              }

              return [rid, null];
            } catch (err) {
              console.error('Error cargando responsable:', rid, err);
              return [rid, null];
            }
          })
        );

        if (!isMounted) return;

        const map = {};
        for (const [rid, info] of responsablesEntries) {
          if (info) map[rid] = info;
        }

        setResponsablesMap(map);
      } catch (err) {
        console.error('Error cargando datos:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [clienteId]);

  const deudasEnriquecidas = useMemo(() => {
    return (deudas || []).map((d) => {
      const esDevolucionCalculada = isDevolucionDeuda(d);

      let totalPagado = 0;
      let saldoCalculado = 0;

      if (esDevolucionCalculada) {
        totalPagado = calcularTotalDevuelto(d);
        saldoCalculado = Math.max(num(calcularPendienteDevolver(d)), 0);
      } else {
        totalPagado = calcularTotalPagadoVenta(d);
        saldoCalculado = calcularSaldoVenta(d);
      }

      const resp = d?.responsableId ? responsablesMap[d.responsableId] : null;
      const responsableNombre = resp ? getNombrePersona(resp) : '';

      const estadoVisual = resolverEstadoDeuda(
        { ...d, esDevolucionCalculada },
        saldoCalculado
      );

      return {
        ...d,
        esDevolucionCalculada,
        totalPagado,
        saldoCalculado,
        responsableNombre,
        responsableOrigen: resp?.origen || null,
        estadoVisual
      };
    });
  }, [deudas, responsablesMap]);

  const { totalMonto, totalPagadoGeneral, totalSaldoRestante } = useMemo(() => {
    return deudasEnriquecidas.reduce(
      (acc, d) => {
        acc.totalMonto += num(getImporte(d));
        acc.totalPagadoGeneral += num(d?.totalPagado);
        acc.totalSaldoRestante += num(d?.saldoCalculado);
        return acc;
      },
      { totalMonto: 0, totalPagadoGeneral: 0, totalSaldoRestante: 0 }
    );
  }, [deudasEnriquecidas]);

  const filtradas = useMemo(() => {
    const f = filtro.toLowerCase().trim();

    return deudasEnriquecidas
      .filter((d) => {
        const searchable = [
          text(d?.concepto),
          text(getImporte(d)),
          text(d?.responsableNombre),
          text(getMensual(d)),
          text(d?.estado),
          text(d?.estadoVisual?.label),
          text(d?.saldoCalculado),
          text(d?.totalPagado)
        ]
          .join(' ')
          .toLowerCase();

        return searchable.includes(f);
      })
      .filter((d) => {
        const key = d?.estadoVisual?.key;

        if (filtroEstado === 'todos') return true;
        if (filtroEstado === 'pendientes') {
          return key === 'pendiente' || key === 'devolucion_pendiente';
        }
        if (filtroEstado === 'pagadas') {
          return key === 'pagada' || key === 'devuelta';
        }
        return true;
      })
      .sort((a, b) => {
        if (orden === 'concepto') {
          return text(a?.concepto).localeCompare(text(b?.concepto), 'es');
        }
        if (orden === 'monto') {
          return num(getImporte(b)) - num(getImporte(a));
        }
        if (orden === 'saldo') {
          return num(b?.saldoCalculado) - num(a?.saldoCalculado);
        }
        if (orden === 'estado') {
          return text(a?.estadoVisual?.label).localeCompare(
            text(b?.estadoVisual?.label),
            'es'
          );
        }
        return 0;
      });
  }, [deudasEnriquecidas, filtro, orden, filtroEstado]);

  const exportCSV = useCallback(() => {
    const escapeCsv = (value) => {
      const str = String(value ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const header = [
      'Concepto',
      'Monto Total',
      'Total Pagado/Devuelto',
      'Saldo Restante',
      'Estado',
      'Responsable'
    ];

    const rows = filtradas.map((d) => [
      text(d?.concepto),
      num(getImporte(d)),
      num(d?.totalPagado),
      num(d?.saldoCalculado),
      text(d?.estadoVisual?.label),
      text(d?.responsableNombre)
    ]);

    const csv = [header, ...rows]
      .map((r) => r.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = `deudas_${clienteId}.csv`;
    a.click();

    URL.revokeObjectURL(url);
  }, [filtradas, clienteId]);

  if (loading) return <div className="deudas-container">Cargando deudas...</div>;

  if (!puedeVerDeudas) {
    return (
      <div className="deudas-container">
        <div className="deudas-header">
          <div>
            <p className="deudas-eyebrow">Acceso restringido</p>
            <h2>No tienes permiso para ver deudas</h2>
            <span className="deudas-resumen">
              Solicita a un administrador activar el permiso de consulta.
            </span>
          </div>

          <button
            className="volver-cliente-btn"
            onClick={() => navigate(`/app/cliente/${clienteId}`)}
          >
            <FaArrowLeft />
            <span>Volver al cliente</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="deudas-container">
      <div className="deudas-header">
        <div>
          <p className="deudas-eyebrow">Cliente</p>
          <h2>
            Deudas de {cliente?.nombre} {cliente?.apellidos}
          </h2>
          <span className="deudas-resumen">
            {filtradas.length} de {deudasEnriquecidas.length} registros visibles
          </span>
        </div>

        <button
          className="volver-cliente-btn"
          onClick={() => navigate(`/app/cliente/${clienteId}`)}
        >
          <FaArrowLeft />
          <span>Volver al cliente</span>
        </button>
      </div>

      <div className="stats deudas-stats">
        <div className="stat-box stat-total">
          <p className="stat-label">Monto Total</p>
          <p className="stat-value">{money(totalMonto)}</p>
        </div>

        <div className="stat-box stat-pagado">
          <p className="stat-label">Pagado / Devuelto</p>
          <p className="stat-value">{money(totalPagadoGeneral)}</p>
        </div>

        <div className="stat-box stat-saldo">
          <p className="stat-label">Saldo Restante</p>
          <p className="stat-value">{money(totalSaldoRestante)}</p>
        </div>
      </div>

      <div className="controls">
        <div className="search-control">
          <FaSearch />
          <input
            type="text"
            placeholder="Filtrar por concepto, monto, responsable o estado"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>

        <select value={orden} onChange={(e) => setOrden(e.target.value)}>
          <option value="concepto">Ordenar por Concepto</option>
          <option value="monto">Ordenar por Monto</option>
          <option value="saldo">Ordenar por Saldo</option>
          <option value="estado">Ordenar por Estado</option>
        </select>

        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
          <option value="todos">Mostrar Todas</option>
          <option value="pendientes">Solo Pendientes</option>
          <option value="pagadas">Solo Pagadas / Devueltas</option>
        </select>

        {puedeExportar && (
          <button className="csv-btn" onClick={exportCSV}>
            <FaFileCsv />
            <span>CSV</span>
          </button>
        )}
      </div>

      <div className="table-wrapper">
        <table className="deudas-table">
          <thead>
            <tr>
              <th>Concepto</th>
              <th>Monto Total</th>
              <th>Total Pagado / Devuelto</th>
              <th>Saldo Restante</th>
              <th>Estado</th>
              <th>Responsable</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {filtradas.map((d) => (
              <tr key={d.id}>
                <td data-label="Concepto">{text(d?.concepto) || '-'}</td>
                <td data-label="Monto Total">{money(getImporte(d))}</td>
                <td data-label="Total Pagado / Devuelto">{money(d?.totalPagado)}</td>
                <td data-label="Saldo Restante">{money(d?.saldoCalculado)}</td>
                <td data-label="Estado">
                  <span className={d?.estadoVisual?.colorClass || 'badge-pendiente'}>
                    {d?.estadoVisual?.label || 'Pendiente'}
                  </span>
                </td>
                <td data-label="Responsable">
                  {d?.responsableNombre || '-'}
                </td>
                <td data-label="Acciones" className="acciones-columna">
                  <Link to={`/app/cliente/${clienteId}/deudas/${d.id}`}>
                    <button className="detalle-btn" title="Ver detalle">
                      <FaSearch />
                    </button>
                  </Link>

                  {puedeRegistrarPago && (
                    <Link to={`/app/cliente/${clienteId}/deudas/${d.id}/pago`}>
                      <button className="pago-btn" title="Registrar pago">
                        <FaMoneyCheckAlt />
                      </button>
                    </Link>
                  )}
                </td>
              </tr>
            ))}

            {filtradas.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', opacity: 0.7 }}>
                  Sin resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DeudasCliente;
